import { useSetTaxChartClassOverride } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  showNotification,
} from '@commenda/kakashi';
import type { TaxChartClass } from '@prisma/client';
import { useState } from 'react';
import { CLASS_LABEL, CLASS_TO_SHAPE, SHAPE_LABEL } from '../tax-chart-shapes';

// Derived so a new class cannot reach the picker without a shape and a name of its own.
// OTHER_REVIEW is auto-fallback only, so it isn't offered as a manual choice.
const OVERRIDE_OPTIONS: { value: TaxChartClass; label: string }[] = (Object.keys(CLASS_TO_SHAPE) as TaxChartClass[])
  .filter((value) => value !== 'OTHER_REVIEW')
  .map((value) => ({ value, label: `${SHAPE_LABEL[CLASS_TO_SHAPE[value]]} — ${CLASS_LABEL[value]}` }));

const OVERRIDE_VALUES = new Set<TaxChartClass>(OVERRIDE_OPTIONS.map((o) => o.value));

interface TaxChartShapeSectionProps {
  entityId: number;
  companyId: number;
  currentClass: TaxChartClass;
  hasOverride: boolean;
  // Lifted so the modal stays mounted when the hover popover closes on mouse-leave.
  modalOpen: boolean;
  onModalOpenChange: (open: boolean) => void;
}

// The tax-chart shape controls, rendered as a footer inside a node's entity-details popover:
// the resolved class + "Change" (opens the modal) and "Reset" (clears the manual override, shown only when one exists).
export function TaxChartShapeSection({ entityId, companyId, currentClass, hasOverride, modalOpen, onModalOpenChange }: TaxChartShapeSectionProps) {
  const [selected, setSelected] = useState<TaxChartClass>();
  const notifyError = (e: unknown) =>
    showNotification({ title: "Couldn't update shape", message: (e as Error).message, valid: false });
  // Notify at the hook (mutation) level so a failed Reset still toasts even if this component unmounts
  // (the popover closes on mouse-leave) before the request settles.
  const setOverride = useSetTaxChartClassOverride(companyId, { onError: notifyError });

  const openModal = () => {
    // Seed only from a selectable class; an auto-only OTHER_REVIEW opens the picker unset so Save stays
    // disabled and we never persist OTHER_REVIEW as a manual override.
    setSelected(OVERRIDE_VALUES.has(currentClass) ? currentClass : undefined);
    onModalOpenChange(true);
  };
  const save = () => {
    if (!selected) return;
    setOverride.mutate({ entityId, taxChartClass: selected }, { onSuccess: () => onModalOpenChange(false) });
  };
  const reset = () => setOverride.mutate({ entityId, taxChartClass: null });

  return (
    <>
      <div className="h-px bg-border-primary w-full" />
      <div className="flex items-center justify-end gap-sm">
        <Tooltip
          content={hasOverride ? 'Revert to the automatic shape.' : 'This entity is already using its automatic shape.'}
          side="top"
          asChild
        >
          <span className="inline-flex">
            <Button variant="secondary" size="xs" type="button" onClick={reset} disabled={!hasOverride || setOverride.isPending}>
              Reset
            </Button>
          </span>
        </Tooltip>
        <Button variant="primary" size="xs" type="button" onClick={openModal}>
          Change shape
        </Button>
      </div>

      <Dialog open={modalOpen} onOpenChange={onModalOpenChange}>
        <DialogContent className="w-[464px]" preventCloseOnOutsideClick>
          <DialogHeader>
            <DialogTitle>Change shape</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2xl p-3xl">
            <p className="txt-small-regular text-text-primary">
              Choose the shape that represents this entity&apos;s tax classification. You can reset
              it at any time.
            </p>
            <div className="flex flex-col gap-xs">
              <label className="txt-small-regular text-text-tertiary">Shape</label>
              <Select value={selected} onValueChange={(v) => setSelected(v as TaxChartClass)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a shape" />
                </SelectTrigger>
                <SelectContent>
                  {OVERRIDE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" size="md" type="button" onClick={() => onModalOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="md" type="button" onClick={save} disabled={setOverride.isPending || !selected}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
